/**
 * The Debits tab: money that left the bank and nothing explains.
 *
 * A debit is explained by a payout, by a transfer returned to its sender, or - here - by what
 * two people say it is. One person says what the debit is: a transfer to a fund's operator
 * (PICKED from the confirmed transfers the fund system reports, never typed), a bank fee, or
 * something else with a note. A manager who is not that person confirms it with an
 * authentication code. The system may propose a transfer when the bank's identifier of the
 * debit equals the transfer's reference exactly; a manager still confirms it.
 *
 * Nothing here books anything: accounting reads the confirmed operator transfers, and a fee or
 * anything else still needs its own entry there.
 */
import { useCallback, useEffect, useState } from 'react';
import StatusBadge from '../../components/StatusBadge';
import bankDebitsService, {
  type DebitExplanationKind,
  type DebitProposal,
  type TransferCandidate,
  type UnexplainedDebit,
} from '../../services/bankDebits.service';
import authService from '../../services/auth.service';
import payoutsService, { type LateDebitTie } from '../../services/payouts.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { cleanMfaInput, isMfaCode, stepUpErrorMessage } from '../../lib/mfaApproval';
import { formatDateTime, formatMoney } from '../../lib/datetime';
import { colors, gradients } from '../../styles/theme';

const inputStyle: React.CSSProperties = {
  background: colors.canvas,
  color: colors.ink,
  border: `1px solid ${colors.border}`,
};

const KIND_LABELS: Record<DebitExplanationKind, string> = {
  operator_transfer: 'Transfer to a fund operator',
  bank_fee: 'Bank fee',
  other: 'Something else',
};

const NOTE_MIN = 5;
const ATTACHMENT_ACCEPT = 'application/pdf,image/png,image/jpeg';
const ATTACHMENT_MAX_BYTES = 1_000_000;

export default function OpsDebitsTab(): JSX.Element {
  const isManager = authService.getRole() !== 'agent';
  const [debits, setDebits] = useState<UnexplainedDebit[]>([]);
  const [proposals, setProposals] = useState<DebitProposal[]>([]);
  const [ties, setTies] = useState<LateDebitTie[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [d, p, t] = await Promise.all([
        bankDebitsService.listUnexplained(), bankDebitsService.listProposals(), payoutsService.lateDebitTies(),
      ]);
      setDebits(d);
      setProposals(p);
      setTies(t);
    } catch (e: unknown) {
      setDebits([]);
      setProposals([]);
      setTies([]);
      setError(getApiErrorMessage(e, 'Could not load the bank debits.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  const done = (message: string): void => {
    setFlash(message);
    globalThis.setTimeout(() => setFlash(null), 6000);
    void reload();
  };

  return (
    <div>
      <p className="text-sm mb-4" style={{ color: colors.muted }}>
        Each debit here left a bank account and no payout, no returned transfer and no confirmed
        explanation accounts for it. One person says what it is; a different manager confirms it.
      </p>
      {flash && (
        <div className="rounded-lg p-3 mb-4 text-sm"
             style={{ background: colors.successBg, color: colors.success, border: `1px solid ${colors.successBorder}` }}>
          {flash}
        </div>
      )}
      {error && (
        <div className="rounded-lg p-3 mb-4 text-sm" role="alert"
             style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}>
          {error}
        </div>
      )}
      {loading && <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>}

      {!loading && !error && (
        <>
          <h3 className="text-sm font-semibold mb-2" style={{ color: colors.ink }}>Waiting for a second person</h3>
          {proposals.length === 0 ? (
            <div className="rounded-2xl p-4 mb-6 text-sm"
                 style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.muted }}>
              No explanation is waiting.
            </div>
          ) : (
            <ul className="space-y-3 mb-6">
              {proposals.map((p) => (
                <ProposalRow key={p.id} proposal={p} isManager={isManager} onDone={done} />
              ))}
            </ul>
          )}

          <h3 className="text-sm font-semibold mb-2" style={{ color: colors.ink }}>Debits nothing explains</h3>
          {debits.length === 0 ? (
            <div className="rounded-2xl p-4 text-sm"
                 style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.muted }}>
              Every debit is explained.
            </div>
          ) : (
            <ul className="space-y-3">
              {debits.map((d) => <DebitRow key={d.bank_transaction_id} debit={d} onDone={done} />)}
            </ul>
          )}

          <h3 className="text-sm font-semibold mt-6 mb-2" style={{ color: colors.ink }}>
            Debits tied to a payout after settlement
          </h3>
          <p className="text-xs mb-2" style={{ color: colors.muted }}>
            The last two months. One manager tied each one on the Payouts screen; review them at month end.
          </p>
          {ties.length === 0 ? (
            <div className="rounded-2xl p-4 text-sm"
                 style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.muted }}>
              No debit was tied after settlement.
            </div>
          ) : (
            <ul className="space-y-2">
              {ties.map((t) => (
                <li key={t.payout_id} className="rounded-2xl p-3 text-xs"
                    style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.inkSoft }}>
                  <span className="font-semibold" style={{ color: colors.ink }}>
                    {formatMoney(t.net_amount, t.currency_code)}
                  </span>
                  {' · ref '}<span className="font-mono" translate="no">{t.funds_reference_number}</span>
                  {' · debit of '}{formatDateTime(t.posted_at)}
                  {' · settled '}{formatDateTime(t.executed_at)}
                  {' · tied '}{formatDateTime(t.debit_tied_at)}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

function DebitRow({ debit, onDone }: Readonly<{ debit: UnexplainedDebit; onDone: (message: string) => void }>): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <li className="rounded-2xl p-4" style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-semibold" style={{ color: colors.ink }}>
            {formatMoney(debit.amount, debit.currency_code)} · {formatDateTime(debit.posted_at)}
          </div>
          <div className="text-xs" style={{ color: colors.muted }}>
            {debit.account_label ?? ''}
            {debit.rail_trace && <> · <span className="font-mono">{debit.rail_trace}</span></>}
          </div>
          {debit.bank_text && (
            <div className="text-xs mt-1 break-words" style={{ color: colors.mutedSoft }}>{debit.bank_text}</div>
          )}
        </div>
        <button type="button" onClick={() => setOpen((v) => !v)}
                className="px-3 py-1.5 rounded-md text-xs font-semibold"
                style={{ background: open ? 'transparent' : gradients.gold, color: open ? colors.inkSoft : colors.navy,
                         border: open ? `1px solid ${colors.border}` : 'none' }}>
          {open ? 'Cancel' : 'Explain this debit'}
        </button>
      </div>
      {open && (
        <ExplainForm
          debit={debit}
          onDone={(message) => { setOpen(false); onDone(message); }}
        />
      )}
    </li>
  );
}

function ExplainForm({ debit, onDone }: Readonly<{ debit: UnexplainedDebit; onDone: (message: string) => void }>): JSX.Element {
  const [kind, setKind] = useState<DebitExplanationKind>('operator_transfer');
  const [candidates, setCandidates] = useState<TransferCandidate[] | null>(null);
  const [picked, setPicked] = useState('');
  const [note, setNote] = useState('');
  const [attachment, setAttachment] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (kind !== 'operator_transfer') return;
    let live = true;
    setCandidates(null);
    setError(null);
    bankDebitsService.transferCandidates(debit.bank_transaction_id)
      .then((list) => {
        if (!live) return;
        setCandidates(list);
        // The one whose reference equals the bank's identifier is offered first, not chosen.
        setPicked('');
      })
      .catch((e: unknown) => {
        if (live) {
          setCandidates([]);
          setError(getApiErrorMessage(e, 'Could not list the fund transfers.'));
        }
      });
    return () => { live = false; };
  }, [kind, debit.bank_transaction_id]);

  const noteOk = kind === 'other' ? note.trim().length >= NOTE_MIN : true;
  // `other` carries evidence: the document, or the bank's own reference on the line.
  const evidenceOk = kind !== 'other' || attachment !== null || debit.has_provider_reference;
  const ready = !busy && noteOk && evidenceOk && (kind !== 'operator_transfer' || picked !== '');

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await bankDebitsService.propose(debit.bank_transaction_id, {
        kind,
        funds_transfer_id: kind === 'operator_transfer' ? picked : null,
        note: note.trim() || null,
        attachment: kind === 'other' ? attachment : null,
      });
      onDone('Explanation proposed. A manager other than you has to confirm it.');
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not propose the explanation.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 pt-3 border-t" style={{ borderColor: colors.border }}>
      {error && (
        <div className="rounded-lg p-2 mb-2 text-xs" role="alert"
             style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}>
          {error}
        </div>
      )}
      <label className="block text-xs font-semibold mb-1" htmlFor={`kind-${debit.bank_transaction_id}`}>
        What is this debit?
      </label>
      <select id={`kind-${debit.bank_transaction_id}`} value={kind}
              onChange={(e) => setKind(e.target.value as DebitExplanationKind)}
              className="px-3 py-2 rounded-lg text-sm mb-3" style={inputStyle}>
        {(Object.keys(KIND_LABELS) as DebitExplanationKind[]).map((k) => (
          <option key={k} value={k}>{KIND_LABELS[k]}</option>
        ))}
      </select>

      {kind === 'operator_transfer' && (
        <fieldset className="mb-3">
          <legend className="text-xs font-semibold mb-1">Confirmed transfer it is</legend>
          {candidates === null && <p className="text-xs" style={{ color: colors.muted }}>Loading…</p>}
          {candidates !== null && candidates.length === 0 && !error && (
            <p className="text-xs" style={{ color: colors.muted }}>
              The fund system has no confirmed transfer to an operator for this amount, this account&apos;s
              fund and these dates that is not already tied to another debit.
            </p>
          )}
          {candidates?.map((c) => (
            <label key={c.transfer_id} className="flex items-center gap-2 text-sm py-1">
              <input type="radio" name={`transfer-${debit.bank_transaction_id}`} value={c.transfer_id}
                     checked={picked === c.transfer_id} onChange={() => setPicked(c.transfer_id)} />
              <span>
                <span className="font-mono">{c.reference_number}</span> · {formatMoney(c.amount, c.currency_code)} · {c.transferred_on}
                {c.trace_matches && <> <StatusBadge tone="success">same identifier as the bank&apos;s</StatusBadge></>}
              </span>
            </label>
          ))}
        </fieldset>
      )}

      {kind !== 'operator_transfer' && (
        <>
          <label className="block text-xs font-semibold mb-1" htmlFor={`note-${debit.bank_transaction_id}`}>
            {kind === 'other' ? 'What is it? (required)' : 'Note (optional)'}
          </label>
          <input id={`note-${debit.bank_transaction_id}`} value={note} maxLength={500}
                 onChange={(e) => setNote(e.target.value)}
                 className="px-3 py-2 rounded-lg text-sm w-full mb-3" style={inputStyle} />
        </>
      )}

      {kind === 'other' && (
        <div className="mb-3">
          <label className="block text-xs font-semibold mb-1" htmlFor={`evidence-${debit.bank_transaction_id}`}>
            {debit.has_provider_reference
              ? 'Document that shows what it is (optional: the bank gave its own reference)'
              : 'Document that shows what it is (required)'}
          </label>
          <input id={`evidence-${debit.bank_transaction_id}`} type="file" accept={ATTACHMENT_ACCEPT}
                 onChange={(e) => {
                   const file = e.target.files?.[0] ?? null;
                   if (file !== null && file.size > ATTACHMENT_MAX_BYTES) {
                     setError('The document is larger than 1 MB.');
                     setAttachment(null);
                     return;
                   }
                   setError(null);
                   setAttachment(file);
                 }}
                 className="text-sm" />
          <p className="text-xs mt-1" style={{ color: colors.muted }}>
            PDF, PNG or JPEG, up to 1 MB. It is kept with the bank records.
          </p>
        </div>
      )}

      <div className="flex justify-end">
        <button type="button" disabled={!ready} onClick={() => { void submit(); }}
                className="px-3 py-1.5 rounded-md text-xs font-semibold"
                style={{ background: gradients.gold, color: colors.navy, opacity: ready ? 1 : 0.6 }}>
          {busy ? '…' : 'Propose'}
        </button>
      </div>
    </div>
  );
}

function ProposalRow({ proposal, isManager, onDone }: Readonly<{
  proposal: DebitProposal;
  isManager: boolean;
  onDone: (message: string) => void;
}>): JSX.Element {
  const [mode, setMode] = useState<'confirm' | 'decline' | null>(null);
  const [code, setCode] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);

  const mayConfirm = isManager && !proposal.proposed_by_me;
  const mayDecline = isManager || proposal.proposed_by_me;
  const ready = !busy && (mode === 'confirm' ? isMfaCode(code) : note.trim().length >= NOTE_MIN);
  const actionLabel = mode === 'confirm' ? 'Confirm explanation' : 'Decline explanation';

  const openAttachment = async (): Promise<void> => {
    setLinkError(null);
    try {
      const url = await bankDebitsService.attachmentLink(proposal.id);
      globalThis.open(url, '_blank', 'noopener,noreferrer');
    } catch (e: unknown) {
      setLinkError(getApiErrorMessage(e, 'Could not open the document.'));
    }
  };

  const run = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      if (mode === 'confirm') {
        await bankDebitsService.confirm(proposal.id, code);
        onDone('Explanation confirmed. The debit is explained.');
      } else {
        await bankDebitsService.decline(proposal.id, note.trim());
        onDone('Explanation declined. The debit can be explained again.');
      }
    } catch (e: unknown) {
      setError(mode === 'confirm' ? stepUpErrorMessage(e, 'Could not confirm.') : getApiErrorMessage(e, 'Could not decline.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="rounded-2xl p-4" style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-semibold" style={{ color: colors.ink }}>
            {formatMoney(proposal.amount, proposal.currency_code)} · {KIND_LABELS[proposal.kind]}
            {proposal.funds_reference_number && <> · <span className="font-mono">{proposal.funds_reference_number}</span></>}
          </div>
          <div className="text-xs" style={{ color: colors.muted }}>
            {proposal.proposal_source === 'rail_trace'
              ? 'Proposed by the system: the bank’s identifier equals the transfer’s reference'
              : `Proposed by ${proposal.proposed_by_name ?? 'a colleague'}`}
            {' · '}{formatDateTime(proposal.proposed_at)}
          </div>
          {proposal.note && <div className="text-xs mt-1" style={{ color: colors.mutedSoft }}>{proposal.note}</div>}
          {proposal.evidence_kind === 'attachment' && (
            <div className="text-xs mt-1" style={{ color: colors.mutedSoft }}>
              <button type="button" className="underline" onClick={() => { void openAttachment(); }}>
                Open the attached document
              </button>
              {proposal.attachment_sha256 && <> · <span className="font-mono">{proposal.attachment_sha256.slice(0, 12)}</span></>}
              {linkError && <span role="alert" style={{ color: colors.danger }}> · {linkError}</span>}
            </div>
          )}
          {proposal.evidence_kind === 'provider_reference' && (
            <div className="text-xs mt-1" style={{ color: colors.mutedSoft }}>
              The bank gave its own reference for this debit.
            </div>
          )}
        </div>
        <div className="flex gap-2">
          {mayConfirm && (
            <button type="button" onClick={() => setMode(mode === 'confirm' ? null : 'confirm')}
                    className="px-3 py-1.5 rounded-md text-xs font-semibold"
                    style={{ background: gradients.gold, color: colors.navy }}>
              Confirm
            </button>
          )}
          {mayDecline && (
            <button type="button" onClick={() => setMode(mode === 'decline' ? null : 'decline')}
                    className="px-3 py-1.5 rounded-md text-xs font-semibold"
                    style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}>
              {proposal.proposed_by_me ? 'Take back' : 'Decline'}
            </button>
          )}
        </div>
      </div>
      {proposal.proposed_by_me && (
        <p className="text-xs mt-2" style={{ color: colors.muted }}>
          You proposed this one: a different manager confirms it.
        </p>
      )}
      {mode && (
        <div className="mt-3 pt-3 border-t" style={{ borderColor: colors.border }}>
          {error && (
            <div className="rounded-lg p-2 mb-2 text-xs" role="alert"
                 style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}>
              {error}
            </div>
          )}
          {mode === 'confirm' ? (
            <input type="text" inputMode="numeric" autoComplete="one-time-code" aria-label="Authentication code"
                   value={code} onChange={(e) => setCode(cleanMfaInput(e.target.value))}
                   placeholder="6-digit code" className="px-3 py-2 rounded-lg text-sm font-mono tracking-widest"
                   style={inputStyle} />
          ) : (
            <input value={note} maxLength={500} onChange={(e) => setNote(e.target.value)}
                   placeholder="Why (min 5 characters)" aria-label="Reason"
                   className="px-3 py-2 rounded-lg text-sm w-full" style={inputStyle} />
          )}
          <div className="flex justify-end mt-2">
            <button type="button" disabled={!ready} onClick={() => { void run(); }}
                    className="px-3 py-1.5 rounded-md text-xs font-semibold"
                    style={{ background: gradients.gold, color: colors.navy, opacity: ready ? 1 : 0.6 }}>
              {busy ? '…' : actionLabel}
            </button>
          </div>
        </div>
      )}
    </li>
  );
}
